﻿/*!
    *
    * Wijmo Library 5.20251.34
    * https://developer.mescius.com/wijmo
    *
    * Copyright(c) MESCIUS inc. All rights reserved.
    *
    * Licensed under the End-User License Agreement For MESCIUS Wijmo Software.
    * us.sales@mescius.com
    * https://developer.mescius.com/wijmo/licensing
    *
    */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { VueApi, WjComponentBehavior } from '@mescius/wijmo.vue2.base';
import * as wjcOlap from '@mescius/wijmo.olap';
var WjPivotGridBehavior = /** @class */ (function (_super) {
    __extends(WjPivotGridBehavior, _super);
    function WjPivotGridBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjPivotGridBehavior.tag = 'wj-pivot-grid';
    WjPivotGridBehavior.props = [
        'isDisabled',
        'tabOrder',
        'newRowAtTop',
        'allowAddNew',
        'allowDelete',
        'allowDragging',
        'allowMerging',
        'allowResizing',
        'allowSorting',
        'allowPinning',
        'autoScroll',
        'autoRowHeights',
        'autoSizeMode',
        'autoGenerateColumns',
        'autoSearch',
        'caseSensitiveSearch',
        'quickAutoSize',
        'bigCheckboxes',
        'childItemsPath',
        'groupHeaderFormat',
        'headersVisibility',
        'showSelectedHeaders',
        'showMarquee',
        'showPlaceholders',
        'itemFormatter',
        'isReadOnly',
        'imeEnabled',
        'mergeManager',
        'selectionMode',
        'showGroups',
        'showSort',
        'showDropDown',
        'showAlternatingRows',
        'showErrors',
        'alternatingRowStep',
        'itemValidator',
        'validateEdits',
        'treeIndent',
        'itemsSource',
        'autoClipboard',
        'expandSelectionOnCopyPaste',
        'frozenRows',
        'frozenColumns',
        'cloneFrozenCells',
        'deferResizing',
        'sortRowIndex',
        'editColumnIndex',
        'stickyHeaders',
        'preserveSelectedState',
        'preserveOutlineState',
        'preserveWhiteSpace',
        'keyActionTab',
        'keyActionEnter',
        'rowHeaderPath',
        'virtualizationThreshold',
        'anchorCursor',
        'lazyRender',
        'refreshOnEdit',
        'copyHeaders',
        'columnGroups',
        'showDetailOnDoubleClick',
        'customContextMenu',
        'collapsibleSubtotals',
        'centerHeadersVertically',
        'showColumnFieldHeaders',
        'showRowFieldHeaders',
        'showValueFieldHeaders',
        'outlineMode',
        'commitEmptyEdits',
        'headersFocusability',
    ];
    WjPivotGridBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'beginningEdit',
        'cellEditEnded',
        'cellEditEnding',
        'prepareCellForEdit',
        'formatItem',
        'resizingColumn',
        'resizedColumn',
        'autoSizingColumn',
        'autoSizedColumn',
        'draggingColumn',
        'draggingColumnOver',
        'draggedColumn',
        'sortingColumn',
        'sortedColumn',
        'pinningColumn',
        'pinnedColumn',
        'resizingRow',
        'resizedRow',
        'autoSizingRow',
        'autoSizedRow',
        'draggingRow',
        'draggingRowOver',
        'draggedRow',
        'deletingRow',
        'deletedRow',
        'loadingRows',
        'loadedRows',
        'rowEditStarting',
        'rowEditStarted',
        'rowEditEnding',
        'rowEditEnded',
        'rowAdded',
        'groupCollapsedChanging',
        'groupCollapsedChanged',
        'columnGroupCollapsedChanging',
        'columnGroupCollapsedChanged',
        'itemsSourceChanging',
        'itemsSourceChanged',
        'selectionChanging',
        'selectionChanged',
        'scrollPositionChanged',
        'updatingView',
        'updatedView',
        'updatingLayout',
        'updatedLayout',
        'pasting',
        'pasted',
        'pastingCell',
        'pastedCell',
        'copying',
        'copied'
    ];
    WjPivotGridBehavior.classCtor = function () { return wjcOlap.PivotGrid; };
    return WjPivotGridBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.olap.PivotGrid} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.olap.PivotGrid} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjPivotGrid = WjPivotGridBehavior.register();
function registerV3WjPivotGrid(app) {
    app.component(WjPivotGridBehavior.tag, WjPivotGrid);
}
var WjPivotChartBehavior = /** @class */ (function (_super) {
    __extends(WjPivotChartBehavior, _super);
    function WjPivotChartBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjPivotChartBehavior.tag = 'wj-pivot-chart';
    WjPivotChartBehavior.props = [
        'isDisabled',
        'tabOrder',
        'chartType',
        'showHierarchicalAxes',
        'showTotals',
        'showTitle',
        'showLegend',
        'legendPosition',
        'stacking',
        'maxSeries',
        'maxPoints',
        'itemsSource',
        'header',
        'footer',
        'headerStyle',
        'footerStyle'
    ];
    WjPivotChartBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput'
    ];
    WjPivotChartBehavior.classCtor = function () { return wjcOlap.PivotChart; };
    return WjPivotChartBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.olap.PivotChart} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.olap.PivotChart} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjPivotChart = WjPivotChartBehavior.register();
function registerV3WjPivotChart(app) {
    app.component(WjPivotChartBehavior.tag, WjPivotChart);
}
var WjPivotPanelBehavior = /** @class */ (function (_super) {
    __extends(WjPivotPanelBehavior, _super);
    function WjPivotPanelBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjPivotPanelBehavior.tag = 'wj-pivot-panel';
    WjPivotPanelBehavior.props = [
        'isDisabled',
        'tabOrder',
        'autoGenerateFields',
        'viewDefinition',
        'engine',
        'itemsSource',
        'showFieldIcons',
        'restrictDragging',
        'deferredUpdate'
    ];
    WjPivotPanelBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'itemsSourceChanged',
        'viewDefinitionChanged',
        'updatingView',
        'updatedView'
    ];
    WjPivotPanelBehavior.classCtor = function () { return wjcOlap.PivotPanel; };
    return WjPivotPanelBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.olap.PivotPanel} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.olap.PivotPanel} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjPivotPanel = WjPivotPanelBehavior.register();
function registerV3WjPivotPanel(app) {
    app.component(WjPivotPanelBehavior.tag, WjPivotPanel);
}
var WjSlicerBehavior = /** @class */ (function (_super) {
    __extends(WjSlicerBehavior, _super);
    function WjSlicerBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjSlicerBehavior.tag = 'wj-slicer';
    WjSlicerBehavior.props = [
        'isDisabled',
        'tabOrder',
        'field',
        'showHeader',
        'header',
        'showCheckboxes',
        'multiSelect'
    ];
    WjSlicerBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput'
    ];
    WjSlicerBehavior.classCtor = function () { return wjcOlap.Slicer; };
    return WjSlicerBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.olap.Slicer} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.olap.Slicer} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjSlicer = WjSlicerBehavior.register();
function registerV3WjSlicer(app) {
    app.component(WjSlicerBehavior.tag, WjSlicer);
}
export function registerOlap(app) {
    if (VueApi.isV3Plus) {
        registerV3WjPivotGrid(app);
        registerV3WjPivotChart(app);
        registerV3WjPivotPanel(app);
        registerV3WjSlicer(app);
    }
}
